import React, { useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowLeft } from "@fortawesome/free-solid-svg-icons";

import AdminNavBar from "../components/AdminNavBar";
import {
  AdminPageBody,
  AdminSectionHeader,
  AdminTablePanel,
  AdminLoadingState,
} from "../components/AdminPageShell";
import { useAdminResourcePersons } from "../store/admin";
import {
  formatDate,
  resourceTitle,
  statusBadgeClass,
} from "./components/resourcePersons/resourcePersonUtils";

// ===== RESOURCE PERSON RESOURCES PAGE — GET /api/admin/resource-persons/:id/resources =====
const ResourcePersonResourcesPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const {
    resourcePersonResourcesData,
    resourcePersonResourcesLoading,
    resourcePersonResourcesError,
    resourcePersonResourcesTotal,
    fetchResourcePersonResources,
  } = useAdminResourcePersons();

  // GET /api/admin/resource-persons/:id/resources
  useEffect(() => {
    if (id) {
      fetchResourcePersonResources(id);
    }
  }, [id, fetchResourcePersonResources]);

  const goBack = () => navigate("/admin/resource-persons");

  // Grouped payload: { courses, books, journals, summary }
  const data = resourcePersonResourcesData || {};
  const summary = data.summary || {};
  const sections = [
    { key: "courses", label: "Courses", items: data.courses || [] },
    { key: "books", label: "Books", items: data.books || [] },
    { key: "journals", label: "Journals", items: data.journals || [] },
  ];

  // Summary cards — matches the /resources payload.
  const summaryCards = [
    { label: "Total Courses", value: summary.totalCourses ?? 0 },
    { label: "Total Books", value: summary.totalBooks ?? 0 },
    { label: "Total Journals", value: summary.totalJournals ?? 0 },
    { label: "Total Resources", value: summary.totalResources ?? resourcePersonResourcesTotal },
  ];

  return (
    <div>
      <AdminNavBar
        title="Resource Person"
        subTitle="Resources"
        context="Browse all content published by this resource person."
      />
      <AdminPageBody>
        <div className="mb-3">
          <button
            onClick={goBack}
            className="mb-2 inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3.5 py-2 text-[13px] font-medium text-slate-700 shadow-sm transition hover:bg-slate-50"
          >
            <FontAwesomeIcon icon={faArrowLeft} /> Back to Resource Persons
          </button>
          <h4 className="mt-2 text-lg font-bold text-slate-900">Resources</h4>
        </div>

        {/* SUMMARY CARDS */}
        <AdminSectionHeader
          title="Resource Summary"
          description="Overview of all content created by this resource person."
          count={`${resourcePersonResourcesTotal} resources`}
        />

        <AdminTablePanel className="mt-4">
          {resourcePersonResourcesLoading ? (
            <AdminLoadingState />
          ) : resourcePersonResourcesError ? (
            <div className="p-4 text-sm text-red-600">{resourcePersonResourcesError}</div>
          ) : (
            <div className="grid grid-cols-1 gap-4 p-4 sm:grid-cols-2 lg:grid-cols-4">
              {summaryCards.map((card) => (
                <div
                  key={card.label}
                  className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm"
                >
                  <p className="m-0 text-xs font-semibold uppercase tracking-wide text-slate-500">
                    {card.label}
                  </p>
                  <p className="mb-0 mt-2 text-xl font-bold text-slate-900">{card.value}</p>
                </div>
              ))}
            </div>
          )}
        </AdminTablePanel>

        {/* COURSES / BOOKS / JOURNALS SECTIONS */}
        {!resourcePersonResourcesLoading &&
          !resourcePersonResourcesError &&
          sections.map((section) => (
            <React.Fragment key={section.key}>
              <div className="mt-6">
                <AdminSectionHeader
                  title={section.label}
                  description={`${section.items.length} ${section.label.toLowerCase()} published by this resource person.`}
                  count={`${section.items.length}`}
                />
              </div>

              <AdminTablePanel className="mt-4">
                {section.items.length === 0 ? (
                  <div className="d-flex justify-content-center align-items-center tableLoader">
                    No {section.label.toLowerCase()} found for this resource person.
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table
                      className="admin-table-responsive w-full border-collapse text-[13px]"
                      style={{ minWidth: "640px" }}
                    >
                      <thead>
                        <tr>
                          <th className="border-b border-slate-200 px-3 py-2.5 text-left text-slate-600">Title</th>
                          <th className="border-b border-slate-200 px-3 py-2.5 text-left text-slate-600">Engagement</th>
                          <th className="border-b border-slate-200 px-3 py-2.5 text-left text-slate-600">Price</th>
                          <th className="border-b border-slate-200 px-3 py-2.5 text-left text-slate-600">Status</th>
                          <th className="border-b border-slate-200 px-3 py-2.5 text-left text-slate-600">Date Added</th>
                        </tr>
                      </thead>
                      <tbody>
                        {section.items.map((item, i) => (
                          <tr key={item.id ?? i} className="border-b border-slate-100 hover:bg-slate-50">
                            <td data-label="Title" className="px-3 py-2.5 font-medium text-slate-800">
                              {resourceTitle(item)}
                            </td>
                            <td data-label="Engagement" className="px-3 py-2.5 text-slate-600">
                              {section.key === "courses"
                                ? `${item.enrollmentCount ?? 0} enrollments`
                                : section.key === "books"
                                  ? `${item.downloads ?? 0} downloads`
                                  : "—"}
                            </td>
                            <td data-label="Price" className="px-3 py-2.5 text-slate-800">
                              {item.price !== undefined && item.price !== null
                                ? `₦${Number(item.price).toLocaleString()}`
                                : item.isFree
                                ? "Free"
                                : "--"}
                            </td>
                            <td data-label="Status" className="px-3 py-2.5">
                              <span className={statusBadgeClass(item.status)}>
                                {item.status || (item.isApproved ? "APPROVED" : "PENDING")}
                              </span>
                            </td>
                            <td data-label="Date Added" className="px-3 py-2.5 text-slate-500">
                              {formatDate(item.uploadedAt)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </AdminTablePanel>
            </React.Fragment>
          ))}
      </AdminPageBody>
    </div>
  );
};

export default ResourcePersonResourcesPage;